/**
 * DG Rebuy cart — shows Shopify's stock message in the Rebuy Smart Cart.
 *
 * Raising a line's quantity past the stock in Rebuy's drawer posts /cart/change.js.
 * Shopify answers 422 "Only 3 items were added to your cart due to availability." and caps
 * the line at what is in stock. Rebuy only logs the error, so the quantity silently stays put
 * (ClickUp 14ypaj0jff0, 19 Oct 2026). Rebuy's drawer template lives in the Rebuy admin, so
 * this watches the drawer's cart requests instead and puts the message under that line.
 *
 * Rebuy calls window.fetch at request time, so wrapping it here (deferred) is early enough.
 * Guard: scripts/qa/rebuy-cart-shows-stock-message.mjs.
 */
(function () {
  'use strict';

  var CART_CHANGE = /\/cart\/change(\.js)?$/;
  var CART_ADD = /\/cart\/add(\.js)?$/;
  var ERROR_CLASS = 'DG-rebuy-cart-error';

  // The message on screen: { key: line item key, text: message }, or null.
  var shown = null;
  var observer = new MutationObserver(render);

  function drawerIsOpen() {
    return document.body.classList.contains('rebuy-cart-visible');
  }

  // The request's fields, from Rebuy's urlencoded string or a JSON/FormData body.
  function fields(body) {
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (e) {
        return Object.fromEntries(new URLSearchParams(body));
      }
    }
    if (body instanceof URLSearchParams || body instanceof FormData) return Object.fromEntries(body);
    return {};
  }

  // The key of the line a /cart/change request names, read before the cart changes.
  function lineKey(params) {
    var cart = window.Rebuy && window.Rebuy.Cart && window.Rebuy.Cart.cart;
    var items = (cart && cart.items) || [];
    if (params.line) return (items[params.line - 1] || {}).key;
    if (params.id) {
      var id = String(params.id);
      return (items.find(function (item) {
        return item.key === id || String(item.variant_id) === id;
      }) || {}).key;
    }
    return undefined;
  }

  // The drawer row for a line. Rebuy renders one row per item it doesn't hide; if the counts
  // disagree (bundles, nested items) the row can't be told, so none is returned.
  function rowFor(key) {
    var smartCart = window.Rebuy && window.Rebuy.SmartCart;
    if (!smartCart || !key) return null;
    var items = smartCart.items().filter(function (item) {
      return !smartCart.isHiddenItem(item);
    });
    var rows = document.querySelectorAll('#rebuy-cart .rebuy-cart__flyout-item');
    if (rows.length !== items.length) return null;
    var index = items.findIndex(function (item) {
      return item.key === key;
    });
    return index < 0 ? null : rows[index];
  }

  // Puts the message under its line, and back again when Rebuy re-renders the drawer.
  function render() {
    var current = document.querySelector('.' + ERROR_CLASS);
    if (!shown) {
      if (current) current.remove();
      return;
    }
    if (!drawerIsOpen()) {
      clear();
      return;
    }
    // On the row, not in its info column: Rebuy positions the quantity buttons absolutely at
    // the bottom of that column, where the message would run under them.
    var row = rowFor(shown.key);
    // Already in place: leave it, so screen readers don't hear it again.
    if (current && current.parentElement === row && current.textContent === shown.text) return;
    if (current) current.remove();
    if (!row) return;

    var message = document.createElement('p');
    message.className = ERROR_CLASS;
    message.setAttribute('role', 'alert');
    message.textContent = shown.text;
    row.append(message);
  }

  function show(key, text) {
    shown = { key: key, text: text };
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    render();
  }

  function clear() {
    shown = null;
    observer.disconnect();
    render();
  }

  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || String(input);
    var path = new URL(url, window.location.href).pathname;
    var isChange = CART_CHANGE.test(path);
    if (!isChange && !CART_ADD.test(path)) return nativeFetch.apply(this, arguments);

    // Read the line before the request: Rebuy refetches the cart as soon as it answers.
    var key = isChange && drawerIsOpen() ? lineKey(fields(init && init.body)) : undefined;
    if (shown) clear();

    var request = nativeFetch.apply(this, arguments);
    if (key) {
      var fallback = window.theme && theme.strings && theme.strings.cartError;
      request
        .then(function (response) {
          if (response.ok) return {};
          // An error page (429, 5xx) may not be JSON.
          return response
            .clone()
            .json()
            .catch(function () {
              return {};
            })
            .then(function (data) {
              var text = data.description || data.message;
              return { text: typeof text === 'string' && text ? text : fallback };
            });
        }, function () {
          // The connection dropped: Rebuy shows nothing for this either.
          return { text: fallback };
        })
        .then(function (result) {
          if (result.text) show(key, result.text);
        });
    }
    return request;
  };
})();

// Rebuy's cart drawer changes the cart outside the theme's cart code, so the header count is refreshed here.
let latestRequest = 0;

document.addEventListener('rebuy:cart.change', async (event) => {
  const count = event.detail?.cart?.cart?.item_count;
  const shown = document.querySelector('#cart-icon-bubble .header__cart-count-number')?.textContent.trim() ?? '0';
  if (count !== undefined && String(count) === shown) return;

  const request = ++latestRequest;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}?sections=cart-icon-bubble`);
    if (!response.ok || request !== latestRequest) return;
    const sections = await response.json();
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (request === latestRequest && cartIconBubble && sections['cart-icon-bubble']) {
      cartIconBubble.innerHTML = sections['cart-icon-bubble'];
    }
  } catch (error) {
    console.error('Cart count refresh failed:', error);
  }
});
